import { GraphQLProvider } from '@codezo/graphql';
import { ReactNode } from 'react';

interface GraphQLWithAuthProps {
	children: ReactNode;
	uri: string;
	token?: string;
}

/**
 * Inner component that has access to auth context
 */
function GraphQLWithAuth({ children, uri, token }: GraphQLWithAuthProps) {
	const handleUnauthorized = () => {
		// Instead of logging out immediately, redirect to re-authentication page
		const currentPath = window.location.pathname;

		// Don't redirect if we're already on auth pages or if we're already redirecting
		if (
			currentPath !== '/auth/reauth' &&
			currentPath !== '/auth/callback' &&
			currentPath !== '/auth/login' &&
			!window.location.href.includes('/auth/reauth')
		) {
			// Pass the return path as a hash parameter like ReAuthenticateCallback expects
			window.location.replace(`/auth/reauth#prevPath=${encodeURIComponent(currentPath)}`);
		}
	};

	return (
		<GraphQLProvider uri={uri} token={token || ''} onUnauthorized={handleUnauthorized}>
			{children}
		</GraphQLProvider>
	);
}

export interface AuthGraphQLProviderProps {
	children: ReactNode;
	uri: string;
	token?: string;
}

/**
 * Combined provider that sets up both Auth and GraphQL contexts
 * with proper integration between them.
 *
 * This provider:
 * 1. Sets up GraphQL client first (for auth mutations)
 * 2. Wraps with AuthProvider
 * 3. Re-wraps with GraphQL client that has auth token access
 *
 * This ensures:
 * - Auth mutations work properly
 * - Auth tokens are included in all requests
 * - Unauthorized errors trigger logout
 */
export function AuthGraphQLProvider({ children, uri, token }: AuthGraphQLProviderProps) {
	return (
		<GraphQLWithAuth uri={uri} token={token}>
			{children}
		</GraphQLWithAuth>
	);
}
